type SentrySdk = typeof import("./sentryClient");

type SentryEnv = {
  dsn?: string;
  environment?: string;
  release?: string;
};

/**
 * Same tag the Investor Portal sets on every event, so these screens report
 * into the same Sentry project, alerts and dashboards as the rest of the
 * sign-in journey. `surface` tells Universal Login apart from the portal.
 */
const INITIAL_TAGS = {
  app: "cx-investor-portal",
  surface: "auth0-universal-login",
  role: "anonymous",
};

/** Transactions this app sends on purpose; everything else is not traced. */
export const SIGN_IN_FORM_READY_TRANSACTION = "sign_in_form_ready";

export const readSentryEnv = (env: ImportMetaEnv): SentryEnv => ({
  dsn: env.VITE_SENTRY_DSN,
  environment: env.VITE_SENTRY_ENVIRONMENT,
  release: env.VITE_SENTRY_RELEASE,
});

let sentryPromise: Promise<SentrySdk | null> | null = null;

/**
 * Loads and initialises Sentry once, off the critical path.
 *
 * The SDK is a separate chunk imported on demand: these screens are what the
 * user waits on to sign in (CX-902), so error reporting must not add to their
 * initial download. The trade-off is that an error thrown before the chunk
 * arrives is not reported.
 *
 * **The DSN is the only switch**, as in the portal: no DSN, no SDK download,
 * no reporting. Resolves to `null` in that case or if the chunk fails to load.
 */
export function loadSentry(
  env: ImportMetaEnv = import.meta.env
): Promise<SentrySdk | null> {
  if (sentryPromise) return sentryPromise;

  const { dsn, environment, release } = readSentryEnv(env);
  if (!dsn) {
    sentryPromise = Promise.resolve(null);
    return sentryPromise;
  }

  sentryPromise = import("./sentryClient")
    .then((Sentry) => {
      Sentry.init({
        dsn,
        environment,
        release,
        // No browserTracingIntegration: only the sign_in_form_ready transaction
        // is wanted, at full rate so its percentiles are exact. It is one event
        // per signed-out visit, which keeps quota predictable.
        tracesSampler: ({ name }) =>
          name === SIGN_IN_FORM_READY_TRANSACTION ? 1 : 0,
        // Session Replay stays off: these screens collect emails and passwords.
        sendDefaultPii: false,
        initialScope: { tags: INITIAL_TAGS },
      });
      return Sentry;
    })
    .catch(() => null);

  return sentryPromise;
}

/** @internal For tests only. */
export function resetSentryForTests(): void {
  sentryPromise = null;
}
